import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/site";
import { trackWhatsApp } from "@/lib/analytics";

function WhatsAppIcon({ className = "w-7 h-7" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}

/**
 * Bulle WhatsApp flottante (pattern NC Factory) — desktop + mobile.
 */
export default function WhatsAppFloat() {
    const [visible, setVisible] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), 1500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!visible || dismissed) return;
        const timer = setTimeout(() => setTooltipOpen(true), 3000);
        return () => clearTimeout(timer);
    }, [visible, dismissed]);

    return (
        <AnimatePresence>
            {visible && (
                <div className="fixed bottom-6 right-6 z-50 flex items-end gap-3">
                    <AnimatePresence>
                        {tooltipOpen && !dismissed && (
                            <motion.div
                                initial={{ opacity: 0, x: 20, scale: 0.9 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 20, scale: 0.9 }}
                                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                className="relative mb-2 max-w-[260px] rounded-2xl border border-line bg-off-white/95 p-4 shadow-lg backdrop-blur-sm"
                            >
                                <button
                                    type="button"
                                    onClick={() => setDismissed(true)}
                                    className="absolute top-2 right-2 rounded-full p-1 text-brown-soft transition-colors hover:bg-brown/5 hover:text-brown"
                                    aria-label="Fermer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                                <div className="flex items-start gap-3 pr-4">
                                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#25D366]/15">
                                        <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium leading-tight text-brown">
                                            Besoin d&apos;un rendez-vous ?
                                        </p>
                                        <p className="mt-1 text-xs text-brown-soft">
                                            Écrivez-moi sur WhatsApp — réponse rapide.
                                        </p>
                                        <a
                                            href={WHATSAPP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackWhatsApp("tooltip")}
                                            className="mt-2.5 inline-block rounded-lg bg-[#25D366] px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#1ebe57]"
                                        >
                                            Démarrer la conversation
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackWhatsApp("fab")}
                        initial={{ opacity: 0, scale: 0, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0, y: 20 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                        onMouseEnter={() => !dismissed && setTooltipOpen(true)}
                        className="group relative"
                        aria-label="WhatsApp — écrire à Joëlle"
                        data-testid="whatsapp-float"
                    >
                        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-20 transition-opacity group-hover:opacity-0" />
                        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-lg transition-transform duration-200 hover:scale-110 hover:bg-[#1ebe57]">
                            <WhatsAppIcon className="h-7 w-7 text-white" />
                        </span>
                    </motion.a>
                </div>
            )}
        </AnimatePresence>
    );
}

export { WhatsAppIcon };
